import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';

/** Widget Builder integration page.
 *
 *  Loads the Widget Builder SDK script, fetches a 24h session from
 *  our backend, and mounts the dashboard + "Add Widget" button via
 *  the SDK. Single dashboardId for now (`atlas-main`) — when we
 *  decide to scope by page/role later, change the prop.
 *
 *  The SDK is loaded once per browser tab via a `data-wb-sdk` script
 *  tag so navigating between routes doesn't reload it. Re-renders
 *  reuse the existing `window.WidgetBuilder` global. */

declare global {
  interface Window {
    WidgetBuilder?: {
      init: (cfg: Record<string, unknown>) => HTMLIFrameElement | undefined;
      addButton: (cfg: Record<string, unknown>) => HTMLButtonElement | undefined;
    };
  }
}

interface SessionResponse {
  sessionId: string;
  expiresAt: string;
  baseUrl: string;
}

interface StatusResponse {
  configured: boolean;
  baseUrl: string | null;
}

export function WidgetPage() {
  const dashboardId = 'atlas-main';
  const dashContainerId = `wb-dashboard-${dashboardId}`;
  const btnContainerId = `wb-add-btn-${dashboardId}`;

  // 1. Is the integration configured at all? Bail early with a friendly
  //    notice if env vars aren't set on this deployment.
  const { data: status, isLoading: statusLoading } = useQuery<StatusResponse>({
    queryKey: ['/widget-builder/status'],
    queryFn: async () => (await api<StatusResponse>('/widget-builder/status')).data!,
  });

  // 2. Mint a 24h session (server hits /api/embed/onboard on first call,
  //    /api/embed/sessions thereafter). Cached for the page life — if
  //    the session expires the user re-loads.
  const { data: session, error: sessionError } = useQuery<SessionResponse>({
    queryKey: ['/widget-builder/session'],
    queryFn: async () => (await api<SessionResponse>('/widget-builder/session', { method: 'POST' })).data!,
    enabled: !!status?.configured,
    staleTime: Infinity,
  });

  // 3. Load the SDK script once. Idempotent — a second component on the
  //    same page reuses the existing tag.
  const [sdkReady, setSdkReady] = React.useState(
    () => typeof window !== 'undefined' && !!window.WidgetBuilder,
  );
  const [sdkError, setSdkError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (sdkReady || !session?.baseUrl) return;
    const existing = document.querySelector<HTMLScriptElement>('script[data-wb-sdk]');
    if (existing) {
      if (window.WidgetBuilder) { setSdkReady(true); return; }
      existing.addEventListener('load', () => setSdkReady(true));
      existing.addEventListener('error', () => setSdkError('Failed to load Widget Builder SDK.'));
      return;
    }
    const s = document.createElement('script');
    s.src = `${session.baseUrl.replace(/\/$/, '')}/embed/sdk.js`;
    s.async = true;
    s.dataset.wbSdk = 'true';
    s.onload = () => setSdkReady(true);
    s.onerror = () => setSdkError('Failed to load Widget Builder SDK.');
    document.head.appendChild(s);
  }, [session?.baseUrl, sdkReady]);

  // 4. Initialize dashboard + "Add Widget" button when both session and
  //    SDK are ready. `inited` ref stops a second mount from re-running
  //    the init (the SDK creates iframes on each call).
  const inited = React.useRef(false);
  React.useEffect(() => {
    if (!session || !sdkReady || !window.WidgetBuilder || inited.current) return;
    inited.current = true;
    window.WidgetBuilder.init({
      sessionId: session.sessionId,
      baseUrl: session.baseUrl,
      container: `#${dashContainerId}`,
      dashboardId,
      height: 'calc(100vh - 220px)',
    });
    window.WidgetBuilder.addButton({
      sessionId: session.sessionId,
      baseUrl: session.baseUrl,
      container: `#${btnContainerId}`,
      dashboardId,
      label: '+ Add Widget',
    });
  }, [session, sdkReady, dashboardId, dashContainerId, btnContainerId]);

  // ── Render states ──────────────────────────────────────────────
  if (statusLoading) {
    return (
      <div className="space-y-3">
        <PageHeader />
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Checking integration status…</CardContent></Card>
      </div>
    );
  }

  if (!status?.configured) {
    return (
      <div className="space-y-3">
        <PageHeader />
        <Card>
          <CardContent className="py-8 text-center text-sm">
            <p className="font-medium mb-2">Widget Builder is not configured.</p>
            <p className="text-muted-foreground">
              An admin needs to set <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">WIDGET_BUILDER_URL</code>,{' '}
              <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">WIDGET_BUILDER_APP_ID</code>, and{' '}
              <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">WIDGET_BUILDER_APP_SECRET</code>{' '}
              in the server environment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="space-y-3">
        <PageHeader />
        <Card>
          <CardContent className="py-8 text-center text-sm">
            <p className="font-medium text-destructive mb-2">Couldn't connect to Widget Builder.</p>
            <p className="text-muted-foreground">{(sessionError as Error).message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sdkError) {
    return (
      <div className="space-y-3">
        <PageHeader />
        <Card>
          <CardContent className="py-8 text-center text-sm">
            <p className="font-medium text-destructive mb-2">Couldn't load the Widget Builder UI.</p>
            <p className="text-muted-foreground">{sdkError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <PageHeader />
        {/* SDK injects the "+ Add Widget" button here — keep an empty
            container ready before the SDK ready check resolves so
            placement doesn't shift when it appears. */}
        <div id={btnContainerId} />
      </div>
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div id={dashContainerId} style={{ minHeight: 600 }}>
            {(!session || !sdkReady) && (
              <div className="py-12 text-center text-sm text-muted-foreground">Loading widgets…</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold flex items-center gap-2">
        <LayoutGrid className="h-5 w-5 text-primary" /> Widgets
      </h1>
      <p className="text-sm text-muted-foreground">
        Build custom dashboards from your Atlas Fleet data. Click <span className="font-medium">+ Add Widget</span> to start from a template, write code, or describe what you want and let AI build it.
      </p>
    </div>
  );
}
